import { UtilityRoomAmenityType, utilityRoomAmenityTypes } from "@instacover/helpers"
import { Button } from "@instacover/ui"
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { Trans } from "react-i18next"
import z from "zod"

import { AmenityIcon } from "@/components/amenities/AmenityIcon"
import { CameraButton } from "@/components/base/CameraButton"
import ImageWrapper from "@/components/base/ImageWrapper"
import ImagesRow from "@/components/ImagesRow"
import { Spinner } from "@/components/Spinner"
import { db, getNextAmenity, setAmenitySkipped } from "@/database/database"
import { Amenity } from "@/database/helpers/tables"
import { useAmenityByType } from "@/database/hooks"
import { photoStepSearchSchema } from "@/helpers/zodSchemes"
import { useSkipAmenity } from "@/hooks/api/useSkipAmenity"
import usePhotoStep from "@/hooks/usePhotoStep"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import PhotoDetailLayout from "@/sections/layouts/PhotoDetailLayout"

const pathParamsSchema = z.object({ amenityType: z.enum(utilityRoomAmenityTypes) })

export const Route = createFileRoute("/$sessionId/_session/_steps/utilityRoom/$amenityType")({
  component: UtilityRoomAmenityRoute,
  params: {
    parse: (rawParams) => pathParamsSchema.parse(rawParams),
  },
  validateSearch: (search) => photoStepSearchSchema.parse(search),
})

function UtilityRoomAmenityRoute() {
  const { sessionId } = Route.useParams()
  const { currentPhotoId } = Route.useSearch()
  const { amenityType } = Route.useParams()

  const { data: amenity, isLoading: amenityIsLoading } = useAmenityByType(sessionId, amenityType)

  // amenity not found
  if (!amenityIsLoading && !amenity) {
    throw notFound()
  }

  // amenity loading
  if (amenityIsLoading || !amenity) {
    return <Spinner position="center" />
  }

  return (
    <AmenityDetail sessionId={sessionId} currentPhotoId={currentPhotoId} amenity={amenity} amenityType={amenityType} />
  )
}

type AmenityDetailProps = {
  sessionId: string
  amenity: Amenity
  amenityType: UtilityRoomAmenityType
  currentPhotoId?: string
}

function AmenityDetail(props: AmenityDetailProps) {
  const { sessionId, currentPhotoId, amenity, amenityType } = props

  const navigate = useNavigate()

  const {
    currentPhoto,
    onAddAnotherPhoto,
    onDeletePhoto,
    photosByAmenityId,
    select,
    resetBeforeAnotherPhoto,
    isModelResultLoading,
  } = usePhotoStep({
    sessionId,
    photoType: "UtilityRoom",
    amenityId: amenity.id,
    currentPhotoId,
  })

  const navigateToNextAmenityOrStep = useCallback(async () => {
    resetBeforeAnotherPhoto()
    const nextAmenity = await getNextAmenity<UtilityRoomAmenityType>(sessionId, "UtilityRoom")

    // finish this step
    if (!nextAmenity) {
      const nextStep = await db.orderedStep.finishAndGetNext({ sessionId, step: "UtilityRoom" })
      if (nextStep) {
        navigate({ to: nextStep.to, params: { ...nextStep.params } })
      }
      return
    }

    // navigate to next amenity
    navigate({
      to: "/$sessionId/utilityRoom/$amenityType",
      params: { sessionId, amenityType: nextAmenity.type },
    })
  }, [navigate, resetBeforeAnotherPhoto, sessionId])

  const { mutateAsync: skipAmenityAsync, isPending: isSkipping } = useSkipAmenity()

  async function onSkipAmenity() {
    await skipAmenityAsync({ sessionId, amenityId: amenity.id, skipped: true })
    await setAmenitySkipped({ amenityId: amenity.id, value: true })
    await navigateToNextAmenityOrStep()
  }

  if (isModelResultLoading) {
    return <Spinner position="center" />
  }

  if (currentPhoto) {
    return (
      <PhotoDetailLayout
        aboveHeading={
          <div>
            {photosByAmenityId && <ImagesRow photos={photosByAmenityId} activePhotoId={currentPhotoId} />}

            <ImageWrapper
              photo={currentPhoto}
              onDelete={() => onDeletePhoto({ photoId: currentPhoto.id, photos: photosByAmenityId ?? [] })}
            />
          </div>
        }
        heading={{
          i18nKey: `amenities.${amenityType}`,
        }}
        description={{
          i18nKey: `utilityRoom.description.${amenityType}`,
        }}
        buttons={
          <>
            <Button
              type="button"
              variant="primary"
              className="mb-4"
              onClick={navigateToNextAmenityOrStep}
              data-testid="amenity-done-btn"
            >
              <Trans i18nKey="general.done" />
            </Button>

            <Button type="button" variant="secondary" onClick={onAddAnotherPhoto}>
              <Trans i18nKey="utilityRoom.ctaAfterPhoto" />
            </Button>
          </>
        }
      />
    )
  }

  /* No photo yet */
  return (
    <BaseLayout
      aboveHeading={<AmenityIcon amenityType={amenityType} />}
      heading={{
        i18nKey: `amenities.${amenityType}`,
      }}
      subheading={{
        i18nKey: "general.howTo",
      }}
      description={{
        i18nKey: `utilityRoom.description.${amenityType}`,
      }}
      buttons={
        <div className="flex w-full justify-center gap-4">
          <Button
            className="w-full px-12 py-3"
            onClick={onSkipAmenity}
            disabled={isSkipping}
            type="button"
            variant="secondary"
            data-testid="amenity-skip-btn"
          >
            <Trans i18nKey="general.skip" />
          </Button>

          <CameraButton onClick={select} disabled={isSkipping} />
        </div>
      }
    />
  )
}
